/**
 * Which sentences of a reading log are actually rendered, and how much empty space stands in for
 * the rest.
 *
 * 日本-602. A log of Alex's own is 1,937 sentences, and `Read.tsx` rendered every one of them:
 * **128,240 DOM elements and ~1.2–1.4 GB of browser memory**, 8.5 s to open, growing ~0.5 MB and
 * ~65 nodes per sentence with no cliff anywhere. `Sentence` draws a span per CHARACTER since
 * 日本-512 — 39,903 of those elements — so the cost per sentence is far higher than a row of text
 * looks.
 *
 * It is paid on **every launch**, not only when that log is opened: `useReading` restores the book
 * you were last reading, so the largest log is the app's resting state.
 *
 * Its own module, pure, for the reason `libraryDrag.ts`, `menuPlacement.ts` and `askScroll.ts` are:
 * `npm test` has no DOM, so a rule left inside the component is covered by reading and by nothing
 * else. `checks/check-read.mjs` is the other half — the geometry this decides is only observable in
 * a browser.
 *
 * **`content-visibility: auto` is not the cheap version of this and was measured, not assumed.**
 * Applied to the rendered log it recovered ~190 MB of ~1,400: it skips layout and paint for
 * off-screen rows and the nodes still exist, which is where the memory is. The only fix is to not
 * create them.
 */

/**
 * A measured row PITCH, by sentence id — the row's own height **plus the gap under it**. Rows that
 * have never rendered are simply absent.
 *
 * The gap is included because the list is a `flex flex-col gap-[22px]`, so consecutive rows sit
 * one gap apart and an offset computed from bare heights drifts by 22px per row — 42,000px over
 * the real log. Measured against a 500-sentence log: 66,218px of content over 502 rows is 131.9
 * per row, which is exactly the 110px row plus the 22px gap.
 *
 * The two spacers are therefore `padding` on the row wrapper and **not** spacer elements: a spacer
 * div would be a flex child and pick up a gap of its own on each side, putting 22px of drift at
 * both ends of every window. With padding the three parts sum to exactly the un-windowed height —
 * `visibleRange`'s last test asserts it.
 */
export type RowHeights = ReadonlyMap<string, number>;

export interface WindowInput {
  /** The scroller's `scrollTop`. */
  scrollTop: number;
  /** The scroller's `clientHeight`. */
  viewportHeight: number;
  /** The ids in render order — the same order `entries` is in. */
  ids: readonly string[];
  /** What has actually been measured. Anything missing falls back to `estimate`. */
  heights: RowHeights;
  /** The stand-in pitch for a row that has never rendered. See `ESTIMATED_ROW_PITCH`. */
  estimate: number;
  /**
   * Extra rows rendered beyond the viewport, each side. Absorbs a fast scroll and — more
   * importantly — means a row is already mounted and measured before it is looked at, so the
   * estimate is wrong for fewer rows over time.
   */
  overscan?: number;
}

export interface WindowRange {
  /** First rendered index, inclusive. */
  start: number;
  /** Last rendered index, EXCLUSIVE, so `ids.slice(start, end)` is the window. */
  end: number;
  /** Height of the spacer standing in for `[0, start)`. */
  padTop: number;
  /** Height of the spacer standing in for `[end, ids.length)`. */
  padBottom: number;
}

/** The default overscan. Six rows is ~800px at the desktop row height. */
export const OVERSCAN = 6;

/**
 * Per-width pitch estimates, **measured** on a real 500-sentence log rather than chosen: 131.9px
 * per row at 1147 (a 110px row + the 22px gap) and 156.7px at 390 (142px + 18px).
 *
 * These only have to be close. Every row that renders is measured and remembered, so the estimate
 * governs the rows nobody has reached yet, and being wrong makes the scrollbar the wrong length and
 * nothing else — it can never hide a sentence, because `visibleRange` places rows by the same
 * arithmetic that sizes the padding.
 */
export const ESTIMATED_ROW_PITCH = { desktop: 132, phone: 157 } as const;

function heightOf(heights: RowHeights, id: string | undefined, estimate: number): number {
  // `id` is widened for `noUncheckedIndexedAccess`: every call site indexes `ids`, and an
  // out-of-range index is the estimate rather than a throw.
  const measured = id === undefined ? undefined : heights.get(id);
  // A zero is a row that was mounted while its container was collapsed — the phone sheet at
  // `peek` does exactly this, and `check-ask.mjs` records the same trap from the other side. Taking
  // it at face value would stack every such row at one offset and make the window unable to
  // advance, so it is treated as unmeasured.
  return measured !== undefined && measured > 0 ? measured : estimate;
}

/**
 * The rows to render for a given scroll position, and the two spacer heights.
 *
 * Walks the list accumulating heights, which is O(n) in the log's length per scroll event — 1,937
 * additions, microseconds, against the 130,000 DOM nodes this exists to avoid. A prefix-sum tree
 * would make it O(log n) and is not worth the second structure to keep in step: the measured cost
 * of the walk is not visible next to one frame of layout.
 *
 * **Never returns an empty window for a non-empty log.** A scroll position past the end — which a
 * shrinking log produces, and which the browser also reports transiently mid-layout — would
 * otherwise render nothing at all, and an empty log body is indistinguishable from a failed load.
 */
export function visibleRange({
  scrollTop,
  viewportHeight,
  ids,
  heights,
  estimate,
  overscan = OVERSCAN,
}: WindowInput): WindowRange {
  const count = ids.length;
  if (count === 0) return { start: 0, end: 0, padTop: 0, padBottom: 0 };

  // A negative `scrollTop` is real: both iOS and a trackpad overscroll report one.
  const top = Math.max(0, scrollTop);
  const bottom = top + Math.max(0, viewportHeight);

  let offset = 0;
  let start = count - 1;
  let end = count;
  let started = false;
  for (let i = 0; i < count; i++) {
    const height = heightOf(heights, ids[i], estimate);
    if (!started && offset + height > top) {
      start = i;
      started = true;
    }
    if (started && offset >= bottom) {
      end = i;
      break;
    }
    offset += height;
  }
  // `started` stays false when every row sits above the viewport, which is the past-the-end case.
  // Anchoring to the last row keeps the bottom of the log rendered, which is where a reader who
  // has scrolled past the end actually is.
  if (!started) start = count - 1;

  start = Math.max(0, start - overscan);
  end = Math.min(count, Math.max(end + overscan, start + 1));

  let padTop = 0;
  for (let i = 0; i < start; i++) padTop += heightOf(heights, ids[i], estimate);
  let padBottom = 0;
  for (let i = end; i < count; i++) padBottom += heightOf(heights, ids[i], estimate);

  return { start, end, padTop, padBottom };
}

/**
 * `heights` with one row's measurement recorded, or the same map when nothing changed.
 *
 * **Returning the identical map on a no-op is the whole point of this being a function.** Every
 * measurement arrives through a `ResizeObserver` that fires on mount for every row in the window,
 * so writing unconditionally would set state on each one and re-render the window again, which
 * fires the observer again. Sub-pixel noise is ignored for the same reason: a row that reports
 * 110.2px and then 110.19px must not restart that loop.
 */
export function heightsWith(heights: RowHeights, id: string, height: number): RowHeights {
  if (height <= 0) return heights;
  const known = heights.get(id);
  if (known !== undefined && Math.abs(known - height) < 0.5) return heights;
  const next = new Map(heights);
  next.set(id, height);
  return next;
}

/**
 * `heights` with every id not in `keep` dropped.
 *
 * A log that is cleared, or a sentence deleted, would otherwise leave its measurement behind for
 * the life of the tab — and since ids are client-minted uuids nothing would ever collide with it.
 * Harmless for a few, unbounded across a long session.
 */
export function heightsFor(heights: RowHeights, keep: readonly string[]): RowHeights {
  if (heights.size === 0) return heights;
  const wanted = new Set(keep);
  let extra = 0;
  for (const id of heights.keys()) if (!wanted.has(id)) extra++;
  if (extra === 0) return heights;
  const next = new Map<string, number>();
  for (const [id, height] of heights) if (wanted.has(id)) next.set(id, height);
  return next;
}

/**
 * The offset of a row from the top of the list, by the same arithmetic `visibleRange` uses.
 *
 * This is what lets `↑`/`↓` reach a row that is not rendered. `scrollIntoView` cannot: there is no
 * element to call it on until the window has moved, and the window only moves in response to the
 * scroll. Sharing the accumulation with `padTop` is what guarantees the two agree — a second
 * traversal that rounded differently would leave the focused row a few pixels under the edge.
 */
export function rowOffset(
  ids: readonly string[],
  heights: RowHeights,
  estimate: number,
  index: number,
): number {
  let offset = 0;
  for (let i = 0; i < Math.min(index, ids.length); i++) {
    offset += heightOf(heights, ids[i], estimate);
  }
  return offset;
}

/**
 * A `scrollTop` that brings row `index` fully into view, or `null` when it already is.
 *
 * Returning `null` for the common case is the point: writing `scrollTop` unconditionally would
 * yank the list on every `↑`/`↓` press within the viewport, so arrowing through what is already on
 * screen would scroll under the reader's eye. Only a row at or past an edge moves anything, and it
 * moves the minimum — the row lands against the edge it came from, never centred, because a
 * centring jump loses the reader's place in the line they were on.
 */
export function scrollToRow(
  input: Omit<WindowInput, "overscan"> & { index: number },
): number | null {
  const { ids, heights, estimate, scrollTop, viewportHeight, index } = input;
  if (index < 0 || index >= ids.length) return null;
  const top = rowOffset(ids, heights, estimate, index);
  const bottom = top + heightOf(heights, ids[index], estimate);
  if (top < scrollTop) return top;
  if (bottom > scrollTop + viewportHeight) return bottom - viewportHeight;
  return null;
}

/**
 * How far beyond the rendered window sentences are parsed, in rows.
 *
 * Larger than `OVERSCAN` on purpose, and they answer different questions: the overscan is what is
 * in the DOM, this is what has its tokens ready when it gets there. A row that scrolls in unparsed
 * still renders — `Sentence` falls back to the raw text — but it renders without furigana or
 * underlines and then gains them, which is a visible flicker rather than a failure. Thirty rows is
 * roughly five screens at the desktop pitch, so an ordinary scroll never outruns it and a drag of
 * the scrollbar across the whole log does, briefly.
 */
export const PARSE_AHEAD = 30;

/** Shared so the default arguments below allocate nothing per call. */
const EMPTY_IDS: ReadonlySet<string> = new Set();

/**
 * The sentences to ask the parser about, given what is on screen and what is already known.
 *
 * **Opening a log used to parse ALL of it before the first row painted** — one `parse-batch` over
 * every sentence, which for 3,000 of them is 2.6 s and a 9.6 MB response, and was essentially the
 * whole of the open once the rendering was windowed (日本-602). Nothing needs those tokens until
 * the row is looked at: the underlines, the hover pivot and the FORM strip are all about a sentence
 * on screen.
 *
 * **The authoritative filter is `useReading.ensureParsed`'s, not this one's.** That is where the
 * record of what has been asked about lives, because it is the thing making the requests; `known`
 * and `inFlight` here are optional and exist so the rule can be tested as a whole and so a caller
 * that does hold such a set can narrow before calling. Read passes neither.
 *
 * `known` means ids a response has already been received for — **whether or not it produced
 * tokens**. A parse that legitimately yields none, and one that failed against an unreachable
 * backend, are both indistinguishable from a row that was never asked about, so without that rule
 * every scroll re-requests the same rows for ever.
 */
export function parseTargets(input: {
  ids: readonly string[];
  start: number;
  end: number;
  known?: ReadonlySet<string>;
  inFlight?: ReadonlySet<string>;
  ahead?: number;
}): string[] {
  const {
    ids, start, end, known = EMPTY_IDS, inFlight = EMPTY_IDS, ahead = PARSE_AHEAD,
  } = input;
  const from = Math.max(0, start - ahead);
  const to = Math.min(ids.length, end + ahead);
  const targets: string[] = [];
  for (let i = from; i < to; i++) {
    const id = ids[i];
    if (id === undefined || known.has(id) || inFlight.has(id)) continue;
    targets.push(id);
  }
  return targets;
}
